import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ContentHeadingData } from 'common/models/content-heading';
import { Subject } from 'rxjs';
import MD5 from 'crypto-js/md5';
import encHex from 'crypto-js/enc-hex';
import { environment } from 'common/environments/environment';
import { EnvName } from 'common/app-constants';

export type SapDecoderPdfType = 'USS' | 'BOM';

export interface SapDecoderPdfResult {
    fileKey: string;
    url: string;
}

export const STAGING_AHU_FILES_BASE_URL = 'https://staging-ahufiles.hvacpartners.com/AHUBuilder';
export const PROD_AHU_FILES_BASE_URL = 'https://ahufiles.hvacpartners.com/AHUBuilder';
export const DECODE_39L_PDF_URL = 'https://files.hvacpartners.com/docs/1001/ChanPart/04/201-12-12.pdf';
export const SPECIAL_ORDER_EMAIL = '39series.quotes@carrier.com';
export const PART_NUMBER_MAX_LENGTH = 18;


@Injectable({ providedIn: 'root' })
export class SapModelNumberDecoderService {
    public breadCrumbData = {
        Content: { title: this.translateService.instant('SAP_PART_NUMBER_DECODER.HEADING') },
        breadCrumbs: [
            {
                title: this.translateService.instant('SAP_PART_NUMBER_DECODER.PARENT_1'),
                url: '/support'
            },
            {
                title: this.translateService.instant('SAP_PART_NUMBER_DECODER.PARENT_2'),
                url: '/support/post-sale support'
            },
            {
                title: this.translateService.instant('SAP_PART_NUMBER_DECODER.PARENT_3'),
                url: '/support/post-sale-support/model-number-decoders'
            }
        ]
    };

  public breadCrumbData$: Subject<ContentHeadingData> = new Subject<ContentHeadingData>();

  constructor(private translateService: TranslateService) { }


  getInvalidMessage(partNumber: string): string | null {
      const pnum = partNumber.toLowerCase();

      if (pnum.slice(0, 3) === '39l') {
          return 'SAP_PART_NUMBER_DECODER.ERRORS.L39_NOT_ONLINE';
      }

      if (pnum.slice(0, 3) === '39m') {
          const flag = pnum.slice(7, 8);
          if (flag === 'b' || flag === 'l' || flag === 'd') {
              return 'SAP_PART_NUMBER_DECODER.ERRORS.SPECIAL_ORDER';
          }
      }

      return null;
  }

  transformPartNumber(partNumber: string): string | null {
      const pnum = partNumber.toLowerCase();

      if (pnum.slice(0, 4) === '39mn' || pnum.slice(0, 4) === '39mw') {
          return this.transform39M(pnum);
      }
      if (pnum.slice(0, 4) === '39rn' || pnum.slice(0, 4) === '39rw') {
          return this.transform39R(pnum);
      }
      if (pnum.slice(0, 4) === '39nc') {
          return pnum;
      }

      return null;
  }

  getFolderCode(fileNameForHash: string): string {
      const hash = MD5(fileNameForHash);
      const hashString = encHex.stringify(hash);

      return hashString.substr(0, 2).toUpperCase();
  }

  buildPdfUrl(fileKey: string, pdfType: SapDecoderPdfType): string {
      const extension = pdfType === 'USS' ? '.USS.pdf' : '.BOM.pdf';
      const hashInput = fileKey.toUpperCase() + extension;
      const folderPath = this.getFolderCode(hashInput);

      const AHU_URL = (environment.envName === EnvName.Staging) ? STAGING_AHU_FILES_BASE_URL : PROD_AHU_FILES_BASE_URL;

      return `${AHU_URL}/${folderPath}/${fileKey}${extension}`;
  }

  resolvePdfUrl(partNumber: string, pdfType: SapDecoderPdfType): SapDecoderPdfResult | null {
      const invalidMessage = this.getInvalidMessage(partNumber);
      if (invalidMessage) {
          return null;
      }

      if (pdfType === 'BOM' && partNumber.toLowerCase().slice(0, 3) !== '39m') {
          return null;
      }

      const fileKey = this.transformPartNumber(partNumber);
      if (!fileKey) {
          return null;
      }

      return {
          fileKey,
          url: this.buildPdfUrl(fileKey, pdfType)
      };
  }

  transform39M(pnum: string): string {
      const reg = /^39m[nw]..[abcd].*/;

      if (reg.test(pnum)) {
          return pnum.slice(0, 4) + pnum.slice(6, 13);
      }

      return pnum.slice(0, 4) + pnum.slice(17, 18) + pnum.slice(7, 13);
  }

  transform39R(pnum: string): string {
      return pnum.slice(0, 4) + pnum.slice(7, 14);
  }

  public setAdminBreadCrumbData(breadCrumb: ContentHeadingData) {
      this.breadCrumbData$.next(breadCrumb);
  }

  public getAdminBreadCrumbData() {
      return this.breadCrumbData$;
  }
}
